import clsx from 'clsx';
import React from 'react';

import { PrimaryButton } from '@components/Button/variants';
import { ModalHeaderProps } from '@components/MarketingModal/common/modalHeader';
import Spinner from '@components/Spinner';
import { useBlueconicContext } from '@contexts/BlueconicContext';
import useArticlesBySlug from '@hooks/useArticlesBySlug';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { captureBlueconicClickEvent } from '@utils/blueconic/utils';
import { getSafeUrl } from '@utils/safe_url';

import RetentionP4ArticleCard from './ArticleCard';

export type MultipleContentImageProps = ModalHeaderProps & {
  articleSlugs?: string[];
  buttonText?: string;
  customUrl?: string;
  modalId?: string | number;
};

const MultipleContentImage = ({
  articleSlugs,
  customUrl,
  title,
  subtitle,
  buttonText,
  modalId,
  modalType,
}: MultipleContentImageProps) => {
  const { trackEvent } = useEventTracking();
  const { variantId } = useBlueconicContext();

  const safeArticleSlugs = articleSlugs || [];
  const { data: articles = [], isLoading } = useArticlesBySlug({ articleSlugs: safeArticleSlugs });
  const safeCustomUrl = getSafeUrl(customUrl, { allowRelative: true });

  const handleEventClick = () => {
    trackEvent(
      click({
        clickLink: safeCustomUrl,
        clickTarget: ClickTarget.AnchorLink,
        clickText: buttonText,
      })
    );
    if (variantId) {
      captureBlueconicClickEvent(variantId);
    }
  };

  return (
    <div className="flex w-full flex-col">
      <div className="px-24 py-32 sm:px-64 md:py-40">
        <h1 className="my-0 font-suisse-works text-headline-xxs text-primary-black sm:text-headline-md md:text-headline-lg">
          {title}
        </h1>
        {isLoading ? (
          <div className="mt-32">
            <Spinner />
          </div>
        ) : (
          <div className="mt-16 flex flex-col gap-24 sm:mt-32 md:mt-28 md:flex-row md:gap-64">
            {articles.slice(0, 3).map((article, index) => (
              <RetentionP4ArticleCard
                key={article.id}
                article={article}
                className={clsx({ 'hidden md:flex': index > 1 })}
                modalId={modalId}
                modalType={modalType}
              />
            ))}
          </div>
        )}
      </div>
      <div className="mt-auto flex flex-col items-center gap-16 bg-grayscale-charcoal px-24 py-24 text-white sm:mt-72 sm:items-start sm:px-[82px] sm:py-24 md:mt-32 md:flex-row md:items-center md:gap-24 md:px-[59px] md:py-[30px]">
        <div className="w-full text-center font-suisse-screen text-body-lg sm:text-left md:w-auto md:text-headline-xxs">
          {subtitle}
        </div>
        {buttonText && safeCustomUrl && (
          <PrimaryButton
            as="link"
            href={safeCustomUrl}
            className="w-fit max-sm:w-full"
            onClickCapture={handleEventClick}
          >
            {buttonText}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export default MultipleContentImage;
